import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Shield,
  Lock,
  Unlock,
  AlertTriangle,
  Users,
  Activity,
  Database,
  Settings,
  UserPlus,
  Search,
  Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authApi } from '../lib/api/auth';
import UserCard from './UserCard';

const AuthManagementSystem = () => {
  // 🔐 אבטחה מתקדמת - רק עם קוד מפתח
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [authAttempts, setAuthAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);

  // מצב הקומפוננטה
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'sessions', 'logs', 'settings'
  const [users, setUsers] = useState([]);
  // const [sessions, setSessions] = useState([]); // לא בשימוש כרגע
  // const [auditLogs, setAuditLogs] = useState([]); // לא בשימוש כרגע
  // const [loading, setLoading] = useState(false); // לא בשימוש כרגע
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  // eslint-disable-next-line no-unused-vars
  const [selectedUsers, setSelectedUsers] = useState([]);

  // מודל יצירת משתמש
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'customer'
  });

  // 🔐 קוד המפתח הסודי (רק בקוד!)
  const SECRET_ACCESS_CODE = 'SHA-AUTH-2025-BEN';
  const MAX_AUTH_ATTEMPTS = 3;
  const LOCKOUT_DURATION = 20 * 60 * 1000; // 20 דקות

  // בדיקת נעילה
  const isLockedOut = () => {
    if (!lockoutUntil) return false;
    return Date.now() < lockoutUntil;
  };

  // אימות קוד גישה
  const handleAuthenticate = () => {
    if (isLockedOut()) {
      const remainingTime = Math.ceil((lockoutUntil - Date.now()) / (1000 * 60));
      toast.error(`מערכת נעולה. נסה שוב בעוד ${remainingTime} דקות`);
      return;
    }

    if (authCode === SECRET_ACCESS_CODE) {
      setIsAuthenticated(true);
      setAuthAttempts(0);
      setLockoutUntil(null);
      toast.success('🔓 גישה מאושרת למערכת ניהול אימותים');
      loadInitialData();
    } else {
      const newAttempts = authAttempts + 1;
      setAuthAttempts(newAttempts);

      if (newAttempts >= MAX_AUTH_ATTEMPTS) {
        setLockoutUntil(Date.now() + LOCKOUT_DURATION);
        toast.error('🔒 יותר מדי ניסיונות שגויים. המערכת נעולה ל-20 דקות');
      } else {
        toast.error(`קוד שגוי. נותרו ${MAX_AUTH_ATTEMPTS - newAttempts} ניסיונות`);
      }
      setAuthCode('');
    }
  };

  // טעינת נתונים ראשונית
  const loadInitialData = async () => {
    await Promise.all([
      loadUsers(),
      loadSessions(),
      loadAuditLogs()
    ]);
  };

  // טעינת משתמשים
  const loadUsers = async () => {
    try {
      setLoading(true);
      const users = await authApi.admin.getAllUsers();
      setUsers(users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('שגיאה בטעינת משתמשים');
    } finally {
      setLoading(false);
    }
  };

  // טעינת סשנים פעילים
  const loadSessions = async () => {
    try {
      const sessions = await authApi.admin.getAllSessions();
      setSessions(sessions || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('שגיאה בטעינת סשנים');
    }
  };

  // טעינת לוגי ביקורת
  const loadAuditLogs = async () => {
    try {
      const logs = await authApi.admin.getAuditLogs();
      setAuditLogs(logs || []);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('שגיאה בטעינת לוגי ביקורת');
    }
  };

  // יצירת משתמש חדש
  const createUser = async () => {
    try {
      if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password) {
        toast.error('נא למלא את כל השדות');
        return;
      }

      await authApi.admin.createUser(newUser);
      toast.success('משתמש נוצר בהצלחה');
      setShowCreateUser(false);
      setNewUser({ firstName: '', lastName: '', email: '', password: '', role: 'customer' });
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('שגיאה ביצירת משתמש');
    }
  };

  // מחיקת משתמש
  const deleteUser = async (userId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק משתמש זה?')) return;

    try {
      await authApi.admin.deleteUser(userId);
      toast.success('משתמש נמחק בהצלחה');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('שגיאה במחיקת משתמש');
    }
  };

  // שינוי תפקיד משתמש
  const updateUserRole = async (userId, newRole) => {
    try {
      await authApi.admin.updateUserRole(userId, newRole);
      toast.success('תפקיד המשתמש עודכן בהצלחה');
      loadUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('שגיאה בעדכון תפקיד המשתמש');
    }
  };

  // סיום סשן
  const terminateSession = async (sessionId) => {
    try {
      await authApi.admin.deleteSession(sessionId);
      toast.success('סשן הופסק בהצלחה');
      loadSessions();
    } catch (error) {
      console.error('Error terminating session:', error);
      toast.error('שגיאה בהפסקת סשן');
    }
  };

  // סינון משתמשים
  const filteredUsers = users && Array.isArray(users) ? users.filter(user => {
    const matchesSearch = (
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  }) : [];

  // מסך אימות
  if (!isAuthenticated) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-gray-900 flex items-center justify-center"
      >
        <motion.div
          initial={{ scale: 0.8, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          className="bg-gray-800 border border-blue-500/20 rounded-2xl p-8 w-full max-w-md shadow-2xl"
        >
          <div className="text-center mb-6">
            <Shield className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">מערכת ניהול אימותים</h2>
            <p className="text-gray-400">גישה מוגבלת למפתחים בלבד</p>
          </div>

          {isLockedOut() && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
              <div className="flex items-center text-red-400">
                <Lock className="w-5 h-5 mr-2" />
                <span>מערכת נעולה זמנית</span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                קוד גישה מתקדם
              </label>
              <input
                type="password"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAuthenticate()}
                disabled={isLockedOut()}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                placeholder="הכנס קוד גישה מתקדם..."
              />
            </div>

            <motion.button
              onClick={handleAuthenticate}
              disabled={isLockedOut() || !authCode.trim()}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
            >
              {isLockedOut() ? (
                <span className="flex items-center justify-center">
                  <Lock className="w-4 h-4 mr-2" />
                  מערכת נעולה
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Unlock className="w-4 h-4 mr-2" />
                  כניסה למערכת
                </span>
              )}
            </motion.button>

            {authAttempts > 0 && !isLockedOut() && (
              <div className="text-center text-sm text-yellow-400">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                נותרו {MAX_AUTH_ATTEMPTS - authAttempts} ניסיונות
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // מסך המערכת הראשי
  return (
    <div className="p-6 bg-gray-900 min-h-screen">
      {/* כותרת */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Users className="w-8 h-8 text-blue-500 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-white">מערכת ניהול אימותים</h1>
            <p className="text-gray-400">ניהול מתקדם של משתמשים וסשנים</p>
          </div>
        </div>

        <button
          onClick={() => {
            setIsAuthenticated(false);
            setAuthCode('');
          }}
          className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          <Lock className="w-4 h-4 mr-2" />
          יציאה מאובטחת
        </button>
      </div>

      {/* טאבים */}
      <div className="flex space-x-4 mb-6 border-b border-gray-700">
        {[
          { id: 'users', label: 'משתמשים', icon: Users },
          { id: 'sessions', label: 'סשנים פעילים', icon: Activity },
          { id: 'logs', label: 'לוגי ביקורת', icon: Database },
          { id: 'settings', label: 'הגדרות אבטחה', icon: Settings }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* תוכן הטאבים */}
      {activeTab === 'users' && (
        <div>
          {/* פעולות משתמשים */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => setShowCreateUser(true)}
              className="flex items-center justify-center py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              יצירת משתמש חדש
            </button>

            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="חיפוש משתמשים..."
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">כל התפקידים</option>
              <option value="admin">מנהלים</option>
              <option value="customer">לקוחות</option>
            </select>

            {selectedUsers.length > 0 && (
              <button
                onClick={() => {/* batch operations */}}
                className="flex items-center justify-center py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                פעולות על {selectedUsers.length}
              </button>
            )}
          </div>

          {/* רשימת משתמשים */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredUsers.map((user) => (
              <UserCard
                key={user._id}
                user={user}
                className="hover:shadow-2xl transition-all duration-300"
              />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'sessions' && (
        <div>
          <h3 className="text-xl font-bold text-white mb-4">סשנים פעילים</h3>
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <p className="text-gray-400">בפיתוח - תצוגת סשנים פעילים</p>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div>
          <h3 className="text-xl font-bold text-white mb-4">לוגי ביקורת אבטחה</h3>
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <p className="text-gray-400">בפיתוח - לוגי ביקורת וניטור</p>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div>
          <h3 className="text-xl font-bold text-white mb-4">הגדרות אבטחה מתקדמות</h3>
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
            <p className="text-gray-400">בפיתוח - הגדרות אבטחה וביקורת</p>
          </div>
        </div>
      )}

      {/* מודל יצירת משתמש */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-md"
          >
            <h3 className="text-xl font-bold text-white mb-4">יצירת משתמש חדש</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">שם פרטי</label>
                <input
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">שם משפחה</label>
                <input
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">אימייל</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">סיסמה</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">תפקיד</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value="customer">לקוח</option>
                  <option value="admin">מנהל</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowCreateUser(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
              >
                ביטול
              </button>
              <button
                onClick={createUser}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
              >
                יצירה
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AuthManagementSystem; 